/**
 * ENG-4642: per-agent / per-day Claude session pinning.
 *
 * The persistent-session manager kills the tmux session on every spawn
 * (clean slate) and starts a fresh `claude` invocation. Pre-this-module,
 * that meant a new conversation every restart — operators lost context
 * any time the manager respawned.
 *
 * Goal: each calendar day is a fresh conversation, but every spawn
 * inside that day reuses the same conversation. We achieve this by
 * generating a stable UUID up front (Claude CLI accepts
 * `--session-id <uuid>` for the first spawn, `--resume <uuid>` for
 * subsequent ones) and persisting it to a tiny per-agent JSON file.
 *
 * Storage: `~/.augmented/<codeName>/daily-session.json` — same root the
 * persistent-session manager already owns via getProjectDir(). Schema:
 *
 *   { "date": "YYYY-MM-DD", "sessionId": "<uuid>", "history": [...] }
 *
 * `history` keeps the last few days' entries so an operator can debug
 * which session was bound to which day. We trim to 7 days so the file
 * doesn't grow unbounded.
 *
 * Day boundary: defaults to host-local date (server timezone). Callers
 * may pass an IANA timezone (e.g. `Australia/Melbourne`) and the
 * rollover will fire at that zone's midnight instead — see ENG-5371.
 * The manager passes the agent's resolved `agentTimezone` (same source
 * as ENG-5363's channel MCP `TZ` env var: `teamSettings.timezone`,
 * defaulting to UTC) so the daily rollover lines up with what an
 * operator in the agent's timezone calls "today".
 *
 * Failure mode: if the on-disk JSONL Claude writes for the resumed
 * session is missing (host moved, profile wiped, claude version
 * incompatibility), `--resume` would fail and the agent would land on
 * the login picker. Callers verify the JSONL exists via
 * `sessionFileExists()` before choosing `--resume`; if it's gone we
 * fall back to `--session-id` (treat the stored UUID as fresh, claude
 * will materialise the JSONL on first turn).
 */

import { randomUUID } from 'node:crypto';
import { existsSync, mkdirSync, readFileSync, readdirSync, renameSync, statSync, writeFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { encodeClaudeProjectPath } from '@augmented/core';

const HISTORY_DAYS = 7;

interface DailySessionEntry {
  date: string; // YYYY-MM-DD
  sessionId: string; // UUID v4
  startedAt: string; // ISO 8601
}

interface DailySessionFile {
  current: DailySessionEntry | null;
  history: DailySessionEntry[];
}

export interface DailySessionResult {
  sessionId: string;
  /** `true` when this call generated a new UUID (first spawn of a new day or first ever). */
  isNew: boolean;
}

function profileDir(codeName: string): string {
  return join(homedir(), '.augmented', codeName);
}

function dailySessionPath(codeName: string): string {
  return join(profileDir(codeName), 'daily-session.json');
}

export function todayLocalIso(now: Date = new Date(), timezone?: string): string {
  // ENG-5371: when an IANA timezone is supplied (e.g. Australia/Melbourne),
  // compute the date in that zone via Intl.DateTimeFormat. Falling back to
  // Date getters preserves the original host-local behaviour for callers
  // (and tests) that don't supply a timezone — important for
  // backward-compatibility with the ENG-4642 contract.
  if (timezone) {
    try {
      const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      // en-CA renders as `YYYY-MM-DD` already — no parts assembly needed.
      // Wrapped in try/catch in case the timezone string is invalid, in
      // which case we fall through to host-local rather than throw.
      return fmt.format(now);
    } catch {
      // Invalid IANA zone — fall back to host-local.
    }
  }
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function readFile(codeName: string): DailySessionFile {
  const path = dailySessionPath(codeName);
  if (!existsSync(path)) return { current: null, history: [] };
  try {
    const raw = readFileSync(path, 'utf-8');
    const parsed = JSON.parse(raw) as Partial<DailySessionFile>;
    return {
      current: parsed.current ?? null,
      history: Array.isArray(parsed.history) ? parsed.history : [],
    };
  } catch {
    // Corrupt file — start fresh rather than crashing the manager.
    return { current: null, history: [] };
  }
}

function writeFile(codeName: string, data: DailySessionFile): void {
  const dir = profileDir(codeName);
  mkdirSync(dir, { recursive: true });
  // Atomic write: tmp + rename. A reader catching us mid-writeFileSync
  // would otherwise see truncated JSON and the corrupt-file branch in
  // readFile() would silently treat the agent as fresh state, losing
  // today's UUID and forcing a rollover the operator didn't ask for.
  // PID + randomUUID in the tmp suffix so two managers (or a respawn
  // racing with its predecessor) can't collide on the temp path and
  // have one rename remove the file the other is about to rename.
  // Mirrors the pattern in restart-flags.ts.
  const finalPath = dailySessionPath(codeName);
  const tmpPath = `${finalPath}.${process.pid}.${randomUUID()}.tmp`;
  writeFileSync(tmpPath, JSON.stringify(data, null, 2), 'utf-8');
  renameSync(tmpPath, finalPath);
}

function trimHistory(
  history: DailySessionEntry[],
  now: Date,
  timezone?: string,
): DailySessionEntry[] {
  // Keep newest first, drop entries older than HISTORY_DAYS by date.
  // Take the injected `now` so callers with a frozen clock (tests
  // walking the day forward) don't get inconsistent cutoffs against
  // `new Date()`. The cutoff is computed in the same timezone as the
  // current entry's date string so equality comparisons hold across DST.
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - HISTORY_DAYS);
  const cutoffIso = todayLocalIso(cutoff, timezone);
  return history.filter((h) => h.date >= cutoffIso).slice(0, HISTORY_DAYS);
}

/**
 * Resolve the session UUID this agent should use right now. Generates
 * (and persists) a new UUID on the first call of a new local day, or
 * when the file is missing/corrupt; otherwise returns the day's
 * existing UUID. Idempotent within the same day.
 *
 * Concurrency: the read-then-write here is not under a file lock.
 * In our deployment the manager runs supervised, one process per
 * host (`agt manager start --supervise` / runSupervisorLoop), so
 * concurrent invocation for the same `codeName` is bounded to the
 * sub-second respawn window when the supervisor restarts the
 * worker. The atomic tmp+rename in writeFile() guarantees we never
 * read torn JSON, so the worst-case under a respawn race is two
 * managers minting different UUIDs and one rename winning — both
 * processes converge on the winner's UUID on the next supervisor
 * tick (which re-reads the file). We've taken that trade-off
 * over a proper inter-process lock because a stale lockfile (from
 * a SIGKILL'd manager) would block all subsequent runs and need
 * its own recovery path; the lossy outcome of a UUID race is one
 * tick of conversation churn, not a permanent block.
 */
export function getOrCreateDailySession(
  codeName: string,
  now: Date = new Date(),
  timezone?: string,
): DailySessionResult {
  const today = todayLocalIso(now, timezone);
  const file = readFile(codeName);

  if (file.current && file.current.date === today) {
    return { sessionId: file.current.sessionId, isNew: false };
  }

  // Roll over: yesterday's (or older) entry moves to history, new one
  // takes its place.
  const next: DailySessionEntry = {
    date: today,
    sessionId: randomUUID(),
    startedAt: now.toISOString(),
  };
  const history = trimHistory(
    [...(file.current ? [file.current] : []), ...file.history],
    now,
    timezone,
  );
  writeFile(codeName, { current: next, history });
  return { sessionId: next.sessionId, isNew: true };
}

/**
 * Record the UUID a caller just spawned with so the day-rollover
 * marker (`current.date`) advances to today.
 *
 * ENG-5431: a spawn that bypasses `getOrCreateDailySession` (today the
 * AGT_DISABLE_SESSION_RESUME path, which mints a fresh `randomUUID()`;
 * under ENG-5397 it was every spawn) never writes `daily-session.json`,
 * so `isStaleForToday()` keeps returning true once the previous
 * `current.date` falls behind — re-firing the day-rollover restart on
 * every supervisor tick. Calling this after each spawn keeps the
 * marker in lockstep with the actual running session. On the ENG-6039
 * resume/fresh paths it's an idempotent no-op.
 *
 * Idempotent: re-calling with the same (date, sessionId) is a no-op
 * write of the same content. Different sessionId on the same date
 * just overwrites `current.sessionId` (the old one moves to history).
 */
export function markDailySessionSpawn(
  codeName: string,
  sessionId: string,
  now: Date = new Date(),
  timezone?: string,
): void {
  const today = todayLocalIso(now, timezone);
  const file = readFile(codeName);
  if (file.current && file.current.date === today && file.current.sessionId === sessionId) {
    return;
  }
  const next: DailySessionEntry = {
    date: today,
    sessionId,
    startedAt: now.toISOString(),
  };
  const history = trimHistory(
    [...(file.current ? [file.current] : []), ...file.history],
    now,
    timezone,
  );
  writeFile(codeName, { current: next, history });
}

/**
 * Reset the day's pin — used as a recovery hatch after `--resume` is
 * rejected by claude (corrupt state, version mismatch). Writes a new
 * UUID for today, demotes the old one to history.
 */
export function rotateDailySession(
  codeName: string,
  now: Date = new Date(),
  timezone?: string,
): string {
  const today = todayLocalIso(now, timezone);
  const file = readFile(codeName);
  const next: DailySessionEntry = {
    date: today,
    sessionId: randomUUID(),
    startedAt: now.toISOString(),
  };
  const history = trimHistory(
    [...(file.current ? [file.current] : []), ...file.history],
    now,
    timezone,
  );
  writeFile(codeName, { current: next, history });
  return next.sessionId;
}

/**
 * Encode an absolute project dir the way Claude Code stores it under
 * ~/.claude/projects/.
 *
 * ENG-8201 (Slice 1) moved the rule itself to `@augmented/core`
 * (`encodeClaudeProjectPath`) so the channel MCP servers can locate the same
 * transcript directory without a second copy of it — see that function for the
 * encoding rules and the ENG-4659 incident that produced them. This stays as a
 * local alias so the module's callers (and `_internals`) read unchanged.
 */
const encodeProjectPath = encodeClaudeProjectPath;

/**
 * Check whether claude has actually written a session JSONL for this
 * UUID. If the file is missing the `--resume` would fail and put the
 * agent on the login picker; callers should fall back to `--session-id`
 * instead. See encodeProjectPath() for the encoding rules.
 */
export function sessionFileExists(
  projectDir: string,
  sessionId: string,
): boolean {
  const path = join(
    homedir(),
    '.claude',
    'projects',
    encodeProjectPath(projectDir),
    `${sessionId}.jsonl`,
  );
  return existsSync(path);
}

/**
 * Directory under ~/.claude/projects/ where Claude Code stores every session
 * transcript for the given project dir. All of an agent's sessions —
 * persistent respawns (one pinned UUID per agent-tz day, ENG-6039, plus
 * rotations), scheduled tasks, and direct-chat invocations — share this one
 * directory because they all run with the same cwd (getProjectDir). The
 * token-usage monitor enumerates it.
 */
export function sessionTranscriptDir(projectDir: string): string {
  return join(homedir(), '.claude', 'projects', encodeProjectPath(projectDir));
}

export function sessionFilePath(projectDir: string, sessionId: string): string {
  return join(sessionTranscriptDir(projectDir), `${sessionId}.jsonl`);
}

/**
 * ENG-6238: age (s) of the current session's transcript JSONL — the wedge
 * detector's "is the model actually producing tokens right now" signal. The
 * transcript grows as the model streams turns/tool calls, so a fresh mtime
 * means real work is happening, where pane.log can be kept fresh by a frozen
 * but animated spinner. Returns null when there's no session id or the file
 * can't be stat'd (the detector then degrades to the pane-age fallback).
 */
export function transcriptActivityAgeSeconds(
  projectDir: string,
  sessionId: string | null,
  now: Date = new Date(),
): number | null {
  if (!sessionId) return null;
  try {
    const mtimeMs = statSync(sessionFilePath(projectDir, sessionId)).mtimeMs;
    return Math.max(0, Math.floor((now.getTime() - mtimeMs) / 1000));
  } catch {
    return null;
  }
}

/**
 * ENG-6294: age (s) of the freshest sub-agent transcript for the current
 * session, or null when there is none. Claude Code writes each sub-agent's
 * transcript to `<transcriptDir>/<sessionId>/subagents/agent-<id>.jsonl` —
 * so while a `run_in_background` worker grinds, ITS jsonl keeps growing even
 * though the parent ended its turn and the main `<sessionId>.jsonl` goes
 * static. A fresh mtime here means the session has a background task doing
 * real work and must not read as wedged (the ENG-6274 dispatch flow's
 * signature is exactly quiet-pane + stale-main-transcript + queued inbound).
 */
export function subagentActivityAgeSeconds(
  projectDir: string,
  sessionId: string | null,
  now: Date = new Date(),
): number | null {
  if (!sessionId) return null;
  const dir = join(sessionTranscriptDir(projectDir), sessionId, 'subagents');
  try {
    let freshestMtimeMs: number | null = null;
    for (const name of readdirSync(dir)) {
      if (!name.endsWith('.jsonl')) continue;
      try {
        const mtimeMs = statSync(join(dir, name)).mtimeMs;
        if (freshestMtimeMs === null || mtimeMs > freshestMtimeMs) freshestMtimeMs = mtimeMs;
      } catch {
        // file vanished between readdir and stat — skip it
      }
    }
    if (freshestMtimeMs === null) return null;
    return Math.max(0, Math.floor((now.getTime() - freshestMtimeMs) / 1000));
  } catch {
    return null; // no subagents dir for this session
  }
}

/**
 * Is the agent's session JSONL idle — i.e. has it not been written for
 * at least `idleSeconds`? Claude appends to the file on every turn
 * (tool calls, assistant messages, user messages) so a stale mtime is
 * a reliable proxy for "nothing in flight". Returns true if the file
 * is missing (no in-flight work to interrupt) or if its mtime is
 * older than the threshold.
 *
 * Used by the scheduled-rollover gate so we don't kill a tmux session
 * mid-task at the day boundary — defer the rollover one tick at a
 * time until the agent is between turns.
 */
export function isAgentIdle(
  projectDir: string,
  sessionId: string,
  idleSeconds = 60,
  now: Date = new Date(),
): boolean {
  const path = sessionFilePath(projectDir, sessionId);
  if (!existsSync(path)) return true;
  try {
    const mtimeMs = statSync(path).mtimeMs;
    return now.getTime() - mtimeMs >= idleSeconds * 1000;
  } catch {
    // stat failed (race, permissions). Treat as non-idle to err on the
    // side of NOT interrupting a possibly-running task.
    return false;
  }
}

/**
 * Cheap "should we roll over?" check for the supervisor tick. Reads
 * the persisted current entry and compares its date against today's.
 * Does NOT mint a new UUID — the caller decides what to do with the
 * answer (typically: kill the tmux session iff isAgentIdle is true,
 * letting the next tick respawn fresh via getOrCreateDailySession).
 */
export function isStaleForToday(
  codeName: string,
  now: Date = new Date(),
  timezone?: string,
): boolean {
  const file = readFile(codeName);
  if (!file.current) return false; // never seeded — nothing to roll
  return file.current.date !== todayLocalIso(now, timezone);
}

/**
 * Minutes elapsed since the local (agent-timezone) start of today, i.e. how
 * far past midnight we are in the agent's day. ENG-7548: the scheduled
 * day-rollover gate defers while an agent is mid-task; a perpetually-busy
 * agent never presents an idle window, so the rollover would defer
 * indefinitely. The caller uses this "how overdue is today's rollover"
 * measure to bound the defer — once it exceeds a grace period the rollover is
 * forced even if the agent looks busy. Timezone-aware via Intl (hourCycle
 * 'h23' so midnight is 00, not 24); falls back to host-local on an invalid
 * zone, mirroring todayLocalIso().
 */
export function minutesSinceLocalMidnight(
  now: Date = new Date(),
  timezone?: string,
): number {
  if (timezone) {
    try {
      const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      });
      const parts = fmt.formatToParts(now);
      const hh = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
      const mm = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
      if (Number.isFinite(hh) && Number.isFinite(mm)) return hh * 60 + mm;
    } catch {
      // Invalid IANA zone — fall back to host-local.
    }
  }
  return now.getHours() * 60 + now.getMinutes();
}

/**
 * Read-only accessor for the current entry, returns null when the
 * file doesn't exist or has no current entry. Useful to grab the
 * sessionId for the idle check without triggering a roll-over write.
 */
export function peekCurrentSession(codeName: string): {
  date: string;
  sessionId: string;
  startedAt: string;
} | null {
  return readFile(codeName).current;
}

/**
 * ENG-9566: snapshot of the per-agent session pin, for archiving on drain.
 *
 * WHY THIS IS ARCHIVED AT ALL. `resolveSessionSpawnDecision` only `--resume`s a
 * conversation when a transcript exists for *today's pinned UUID*. That pin
 * lives here, at `~/.augmented/<codeName>/daily-session.json`, and it is swept
 * by none of the three archivers: agt-session-archiver.sh covers
 * `/root/.claude/projects` plus a `-maxdepth 1 -type f -name 'manager.log-*'`
 * sweep of `/root/.augmented` (whose comment excludes per-agent subdirectories
 * explicitly), and the drain flush covers `~/.claude/projects`. So a destination
 * host that restored every transcript byte-for-byte would still mint a fresh
 * `randomUUID()` and treat all of them as orphans. Archiving the pin alongside
 * the transcripts is what makes a restored conversation resumable at all.
 *
 * Under the id-keyed layout `~/.augmented/<codeName>` is a symlink to the real
 * `~/.augmented/<agentId>` dir, so reading through the codename path lands on
 * the same file either way — no layout branch is needed here.
 *
 * `raw` is kept verbatim so a later schema change loses nothing; the two parsed
 * fields are the convenience a restore actually reads. A corrupt or unreadable
 * file keeps `present: true` and nulls the parsed fields rather than reporting
 * absence — "unparseable" and "never existed" are different problems for
 * whoever debugs a failed restore, and collapsing them hides the first.
 */
export interface DailySessionPinSnapshot {
  /** Absolute path the pin was read from (recorded for debugging a restore). */
  path: string;
  /** The file exists. False means this agent has no pin at all. */
  present: boolean;
  /** Verbatim file contents; null when present but unreadable. */
  raw: string | null;
  /** Parsed `current.date` (YYYY-MM-DD in the agent's timezone), if any. */
  currentDate: string | null;
  /** Parsed `current.sessionId` — the UUID a destination must `--resume`. */
  currentSessionId: string | null;
}

export function readDailySessionPin(codeName: string): DailySessionPinSnapshot {
  const path = dailySessionPath(codeName);
  if (!existsSync(path)) {
    return { path, present: false, raw: null, currentDate: null, currentSessionId: null };
  }
  let raw: string;
  try {
    raw = readFileSync(path, 'utf-8');
  } catch {
    // Unreadable (permissions, or a teardown race) — present but uncaptured.
    return { path, present: true, raw: null, currentDate: null, currentSessionId: null };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<DailySessionFile>;
    const current = parsed.current ?? null;
    return {
      path,
      present: true,
      raw,
      currentDate: current?.date ?? null,
      currentSessionId: current?.sessionId ?? null,
    };
  } catch {
    // Corrupt JSON: keep the bytes so a human can still recover the UUID.
    return { path, present: true, raw, currentDate: null, currentSessionId: null };
  }
}

// Exported for unit tests — keep the surface small.
export const _internals = { todayLocalIso, dailySessionPath, profileDir, encodeProjectPath };
